'use client';
import { useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { fetchProfileRole, portalIdentities, setStoredRole, SET_PASSWORD_PATH } from '@/lib/roleSession';

export const AUTH_LINK_ERROR_KEY = 'cbs-auth-link-error';

const LINK_TYPES = ['invite', 'magiclink', 'recovery', 'signup', 'email_change'];

function cleanUrl() {
  try {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('code');
    url.searchParams.delete('type');
    url.searchParams.delete('error');
    url.searchParams.delete('error_code');
    url.searchParams.delete('error_description');
    window.history.replaceState(null, '', url.pathname + (url.search ? url.search : ''));
  } catch {
    // ignore
  }
}

export default function AuthLinkHandler() {
  const router = useRouter();
  const pathname = usePathname();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    if (typeof window === 'undefined') return;

    const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
    const hashParams = new URLSearchParams(hash);
    const searchParams = new URLSearchParams(window.location.search);

    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const hashType = hashParams.get('type');
    const code = searchParams.get('code');
    const hashError = hashParams.get('error') || hashParams.get('error_code') || searchParams.get('error_code');

    const hasTokenLink = Boolean(accessToken && refreshToken) || (hashType && LINK_TYPES.includes(hashType) && accessToken);
    const hasLink = Boolean(hasTokenLink || code || hashError);
    if (!hasLink) return;

    // Leave the dedicated confirm/callback routes alone; they manage their own flow
    if (pathname?.startsWith('/auth/')) return;

    ran.current = true;

    const fail = () => {
      cleanUrl();
      try {
        sessionStorage.setItem(AUTH_LINK_ERROR_KEY, '1');
      } catch {
        // ignore
      }
      router.replace('/login?link=expired');
    };

    (async () => {
      const supabase = createClient();
      try {
        if (hashError && !accessToken && !code) {
          fail();
          return;
        }

        let linkType: string | null = hashType;

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            fail();
            return;
          }
        } else if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
          if (error) {
            fail();
            return;
          }
        }

        const { data } = await supabase.auth.getUser();
        const user = data?.user;
        if (!user) {
          fail();
          return;
        }

        cleanUrl();

        const needsPassword =
          user.user_metadata?.must_set_password === true || linkType === 'invite' || linkType === 'recovery';
        if (needsPassword) {
          router.replace(SET_PASSWORD_PATH);
          return;
        }

        const role = (await fetchProfileRole(user.id).catch(() => null)) ?? 'member';
        setStoredRole(role);
        router.replace(portalIdentities[role].home);
      } catch {
        fail();
      }
    })();
  }, [pathname, router]);

  return null;
}
